import { h } from 'preact';
import { cn } from '../../../utils/helpers';
import { useState, useRef, useEffect } from 'preact/hooks';

/**
 * Tag Component
 * Renders a customizable tag with various styles, sizes, and optional icons.
 *
 * @param {Object} props - Component props
 * @param {string} [props.label=''] - Text displayed on the tag
 * @param {"default" | "primary" | "secondary" | "tertiary" | "muted" | "lightPrimary" | "inverse" | "inverse-transparent" | "club" | "quaternary" | "tagLight" | "tagDark" | "marketplace" | "sponsored" | "freedelivery" | "brandDealFilled" | "brandDealInverse" | "brandDealAlt"} [props.variant='default'] - Visual style of the tag
 * @param {"small" | "default" | "responsive" | "tip" | "freedelivery" | "deal"} [props.size='default'] - Tag size
 * @param {JSX.Element | null} [props.icon] - Icon displayed when set; takes precedence over iconleft
 * @param {JSX.Element | Function | null} [props.iconleft=null] - Icon displayed to the left of the label
 * @param {boolean} [props.tip=null] - When true, applies tip border styling
 * @param {string} [props.date] - Optional date text rendered after the label
 * @param {string} [props.tooltiplabel=''] - Tooltip text shown on hover (desktop) or tap (touch)
 * @param {string} [props.classname] - Additional classes to add to the component
 * @returns {JSX.Element} The Tag component
 */

const Tag = ({
  label = '',
  variant = 'default',
  size = 'default',
  icon,
  iconleft = null,
  tip = null,
  date,
  classname,
  tooltiplabel = '',
  ...props
}) => {
  // Tailwind CSS Classes
  const CLASSES = {
    base: 'tw:relative tw:inline-block tw:no-underline tw:inline-flex tw:items-center tw:rounded-[120px] tw:focus:outline-hidden tw:transition-all tw:leading-none tw:text-black-1000 tw:cursor-default',
    sizes: {
      small: 'tw:px-1 tw:text-[11px]',
      default: 'tw:px-3 tw:py-2 tw:w-max tw:text-base',
      responsive: 'tw:p-2 tw:text-sm tw:sm:px-3 tw:sm:w-max tw:sm:text-base',
      tip: 'tw:p-2 tw:text-base',
      deal: 'tw:h-6 tw:lg:h-8 tw:gap-1 tw:lg:gap-2 tw:px-2 tw:lg:px-3 tw:text-sm tw:lg:text-base tw:w-max',
    },
    variants: {
      default: 'tw:bg-primary-500 tw:text-white',
      primary: 'tw:bg-tag-default tw:text-black',
      secondary: 'tw:bg-secondary-1100 tw:text-black',
      tertiary: 'tw:bg-grey-600 tw:text-black',
      muted: 'tw:bg-transparent tw:text-grey-600',
      lightPrimary: 'tw:bg-primary-100 tw:text-brand-aa',
      inverse: 'tw:bg-white tw:text-black tw:border tw:border-black',
      club: 'tw:text-white tw:bg-blue-600',
      quaternary: 'tw:bg-primary-600 tw:text-white',
      tagLight:
        'tw:bg-tag-light-bg tw:text-tag-light-text tw:border tw:border-tag-light-border',
      tagDark: 'tw:bg-tag-dark-bg tw:text-tag-dark-text',
      'inverse-transparent':
        'tw:bg-transparent tw:text-black tw:border tw:border-black',
      marketplace:
        'tw:bg-white tw:border tw:border-primary-500 tw:text-primary-600',
      sponsored: 'tw:bg-brand-aa tw:text-white',
      freedelivery: 'tw:bg-beige-1000 tw:font-semibold',
      brandDealFilled:
        'tw:bg-brand-deal tw:text-white tw:hover:bg-brand-deal-hover',
      brandDealInverse:
        'tw:bg-white tw:text-brand-deal tw:border-2 tw:border-brand-deal tw:hover:text-brand-deal-hover tw:hover:border-brand-deal-hover',
      brandDealAlt: 'tw:bg-brand-deal-light',
    },
    iconSize: {
      default: 'tw:p-2',
    },
    tip: 'tw:border tw:border-[1.5px] tw:rounded-[120px] tw:font-medium',
    text: {
      base: 'tw:px-0.5 tw:font-semibold tw:block tw:leading-[20px]',
      secondary: 'tw:px-0.5 tw:font-bold',
      quaternary: 'tw:px-2 tw:font-medium',
      muted: 'tw:font-light',
      lightPrimary: 'tw:font-bold',
      club: 'tw:font-normal',
      tagLight: 'tw:font-medium',
      tagDark: 'tw:font-bold',
      'inverse-transparent': 'tw:px-2 tw:font-medium tw:py-3',
      marketplace: 'tw:font-bold',
      sponsored: 'tw:font-bold',
      brandDealFilled: 'tw:font-medium',
      brandDealInverse: 'tw:font-medium',
      brandDealAlt: 'tw:font-medium',
    },
  };

  // Determine the classes to apply based on the props
  const sizeClass = label ? CLASSES.sizes[size] : CLASSES.iconSize[size];
  const variantClass = CLASSES.variants[variant];

  const [open, setOpen] = useState(false);
  const tagRef = useRef(null);
  // Detect touch device
  const isTouch = typeof window !== 'undefined' && 'ontouchstart' in window;
  // Close tooltip on outside click (for mobile)
  useEffect(() => {
    if (!open || !isTouch) return;

    const handleClick = (e) => {
      if (tagRef.current && !tagRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, isTouch]);

  return (
    <div
      ref={tagRef}
      className={cn(
        CLASSES.base,
        sizeClass,
        variantClass,
        tip && CLASSES.tip,
        classname,
      )}
      {...props}
      onMouseEnter={() => tooltiplabel && !isTouch && setOpen(true)}
      onMouseLeave={() => tooltiplabel && !isTouch && setOpen(false)}
      onClick={() => tooltiplabel && isTouch && setOpen((v) => !v)}
      tabIndex={tooltiplabel ? 0 : undefined}
      aria-label={label}
    >
      {icon && <span className="tw:px-0.5 tw:text-inherit">{icon}</span>}
      {!icon && iconleft && (
        <span className="tw:pr-2 tw:text-inherit">
          {typeof iconleft === 'function' ? iconleft() : iconleft}
        </span>
      )}
      <span className="py-0.5 tw:flex tw:items-center tw:gap-1 tw:px-1 tw:py-0.5">
        {label && (
          <span className={cn(CLASSES.text.base, CLASSES.text[variant])}>
            {label}
          </span>
        )}
        {date && <span>{date}</span>}
      </span>
      {/* Tooltip */}
      {tooltiplabel && open && (
        <div className="font-inter tw:absolute tw:top-auto tw:bottom-[calc(100%+4px)] tw:left-0 tw:z-10 tw:rounded-lg tw:bg-white tw:px-3 tw:py-2 tw:text-[14px] tw:leading-tight tw:font-medium tw:whitespace-nowrap tw:text-black tw:shadow-[6px_10px_24px_rgba(0,0,0,0.3)]">
          {tooltiplabel}
        </div>
      )}
    </div>
  );
};

export default Tag;
